// My Garage tour - /parts-finder/my-garage (guest gate, vehicle list, Add-vehicle modal).
import { path, visible, exists, loggedIn } from './util.js';

function signedIn() { return loggedIn() && exists('#root [data-tour="garage-header"]'); }
function hasVehicles() { return exists('#root [data-tour="garage-card"]'); }
function modalOpen() { return !!visible('[data-tour="avm-vehicle"]'); }
function isB2B() { return exists('#root [data-tour="garage-labels"]'); }
function firstCard() { return visible('#root [data-tour="garage-card"]'); }
function inFirstCard(sel) { return function () { var c = firstCard(); return c ? c.querySelector(sel) : null; }; }

export default {
  id: 'my-garage',
  version: 1,
  title: 'My Garage',
  icon: 'garage',
  duration: '40 sec',
  available: function () { return path() === '/parts-finder/my-garage'; },
  readySelector: '#root [data-tour="garage-header"], #root a[href="/account/login"]',
  invite: {
    kicker: 'My Garage',
    title: 'Save your vehicles, skip the guesswork',
    body: 'See how to save a vehicle once and find parts that fit it in a tap, every time.',
  },
  steps: [
    {
      title: 'Welcome to My Garage',
      body: 'Save the vehicles you drive or service. Pick one and every search across the site is matched to it.',
      icon: 'garage',
      hero: 'intro',
    },

    // ── Guest ────────────────────────────────────────────────────────────
    {
      when: function () { return !signedIn(); },
      target: '#root a[href="/account/login"]',
      icon: 'lock',
      title: 'Sign in to unlock it',
      body: 'My Garage is linked to your Goldwagen account, so your vehicles follow you to any device. Sign in, or create a free account, to start.',
      optional: true,
    },

    // ── Signed in ────────────────────────────────────────────────────────
    {
      when: signedIn,
      target: '#root [data-tour="garage-header"]',
      icon: 'garage',
      title: 'Your vehicles in one place',
      body: 'The count shows how many vehicles you have saved. A warning icon means one still needs a few details.',
    },
    {
      when: function () { return signedIn() && exists('#root [data-tour="garage-labels"]'); },
      target: '#root [data-tour="garage-labels"]',
      icon: 'tag',
      title: 'Organise with labels',
      body: 'Group vehicles by customer, fleet or job status. Tap a label to filter, or <b>Manage</b> to create and rename them.',
      optional: true,
      waitFor: 800,
    },
    {
      when: function () { return signedIn() && !hasVehicles(); },
      target: '#root [data-tour="garage-empty"]',
      icon: 'car',
      title: 'Your garage is empty',
      body: 'No vehicles yet. Add your first one in a moment, and it only takes make and model to get going.',
      optional: true,
    },
    {
      when: function () { return signedIn() && exists('#root [data-tour="garage-search"]'); },
      target: '#root [data-tour="garage-search"]',
      icon: 'search',
      title: 'Find a vehicle fast',
      body: 'With a few vehicles saved, search by nickname, make, model or registration.',
      optional: true,
      waitFor: 800,
    },
    {
      when: function () { return signedIn() && hasVehicles(); },
      target: firstCard,
      icon: 'car',
      title: 'Each vehicle at a glance',
      body: 'The nickname, make, model and year, plus how sure you are of the details. A <b>Not complete yet</b> tag means some details are still missing.',
      padding: 6,
    },
    {
      when: function () { return signedIn() && hasVehicles(); },
      target: inFirstCard('[data-tour="garage-search-parts"]'),
      icon: 'search',
      title: 'Search Parts',
      body: 'Opens the Parts Finder already filtered to this vehicle, and makes it your active vehicle across the site.',
      optional: true,
    },
    {
      when: function () { return signedIn() && hasVehicles(); },
      target: function () { var c = firstCard(); var e = c && c.querySelector('[title="Edit vehicle"]'); return e ? e.parentElement : null; },
      icon: 'pencil',
      title: 'Edit or remove',
      body: 'Update a vehicle’s details (VIN, registration, licence disc) or remove it from your garage.',
      optional: true,
      padding: 6,
    },
    {
      when: signedIn,
      target: function () { return visible('#root [data-tour="garage-add"]'); },
      icon: 'plus',
      title: 'Add a vehicle',
      body: 'Open the form to save a new vehicle. Want to see it? Tap <b>Add</b>, or press Next to skip.',
      interactive: {
        on: 'click',
        hint: 'Tap “Add” to open the form',
        done: 'Here is the form.',
        delay: 700,
      },
    },

    // ── Add-vehicle modal (only when the customer opened it) ─────────────
    {
      when: modalOpen,
      count: true,
      target: '[data-tour="avm-vehicle"]',
      icon: 'car',
      title: 'The vehicle itself',
      body: 'Make and model are required. Variant and year narrow the parts down to an exact fit.',
      padding: 8,
      hideBack: true,
    },
    {
      when: function () { return modalOpen() && exists('[data-tour="avm-customer"]'); },
      count: isB2B,
      target: '[data-tour="avm-customer"]',
      icon: 'user',
      title: 'Whose vehicle is it?',
      body: 'For workshops: record the customer’s name and contact details, so you can find their vehicle by name later.',
      optional: true,
    },
    {
      when: function () { return modalOpen() && exists('[data-tour="avm-labels"]'); },
      count: isB2B,
      target: '[data-tour="avm-labels"]',
      icon: 'tag',
      title: 'Label it',
      body: 'Assign labels such as <b>In Workshop</b> or a fleet name. You can add a new label right here.',
      optional: true,
    },
    {
      when: modalOpen,
      count: true,
      target: '[data-tour="avm-friendly-name"]',
      icon: 'tag',
      title: 'Give it a nickname',
      body: '“The bakkie”, “Wife’s car”, or a customer’s name for workshops. Whatever helps you recognise it.',
      optional: true,
    },
    {
      when: modalOpen,
      count: true,
      target: '[data-tour="avm-confidence"]',
      icon: 'badge',
      title: 'How sure are you?',
      body: 'Tell us how confident you are in the details. Vehicles you are unsure of are flagged so you can confirm them later.',
      optional: true,
    },
    {
      when: modalOpen,
      count: true,
      target: '[data-tour="avm-identifiers"]',
      icon: 'list',
      title: 'VIN, registration & licence',
      body: 'Optional, but the VIN and engine number help our team confirm exact-fit parts. We will remind you when the licence disc expires.',
      optional: true,
    },
    {
      when: modalOpen,
      count: true,
      target: '[data-tour="avm-uploads"]',
      icon: 'camera',
      title: 'Add a photo',
      body: 'Upload a licence disc or a photo of the vehicle so it is easy to spot in your garage.',
      optional: true,
    },
    {
      when: modalOpen,
      count: true,
      target: '[data-tour="avm-save"]',
      icon: 'check',
      title: 'Save it',
      body: 'Save now and finish the rest later. Incomplete vehicles still work for searching.',
      optional: true,
    },
    {
      title: 'Your garage is ready',
      body: 'Choose a vehicle, tap <b>Search Parts</b>, and only parts that fit will show.',
      hero: 'success',
      nextLabel: 'Done',
    },
  ],
};
